
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "../../utils/axios";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumb from "../../components/ui/Breadcrumb";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";

const API = {
  contracts: "/contracts",
  types: "/contract-types",
  categories: "/contract-categories",
  users: "/admin/users",
};

const EMPTY_FORM = {
  contract_no: "",
  title: "",
  contract_type_id: "",
  contract_category_id: "",
  supplier: "",
  start_date: "",
  end_date: "",
  renewal_date: "",
  value: "",
  status: "Draft",
  owner: "",
  department: "",
  assets_covered: "0",
  description: "",
};

const STATUSES = [
  "Draft",
  "Active",
  "Renewal Due",
  "Expiring Soon",
  "Expired",
  "Suspended",
];

const unwrap = (response) => response?.data ?? response;

const getRows = (response) => {
  const body = unwrap(response);
  const data = body?.data ?? body;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;

  return [];
};

const getErrorMessage = (error) => {
  const errors = error?.response?.data?.errors;

  if (errors) {
    return Object.values(errors).flat().join(" ");
  }

  return (
    error?.response?.data?.message ||
    error?.message ||
    "Something went wrong. Please try again."
  );
};

const getLabel = (item) =>
  item?.name ?? item?.title ?? item?.label ?? `#${item?.id}`;

const formatDate = (value) => {
  if (!value) return "—";

  const dateValue = String(value).slice(0, 10);
  const parsed = new Date(`${dateValue}T00:00:00`);

  return Number.isNaN(parsed.getTime())
    ? "—"
    : parsed.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

const formatMoney = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const getDateStatus = (contract) => {
  if (["Draft", "Suspended", "Expired"].includes(contract.status)) {
    return contract.status;
  }

  if (!contract.end_date) return contract.status || "Draft";

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const endDate = new Date(
    `${String(contract.end_date).slice(0, 10)}T00:00:00`
  );

  if (Number.isNaN(endDate.getTime())) {
    return contract.status || "Draft";
  }

  if (endDate < today) return "Expired";

  const daysRemaining = Math.ceil(
    (endDate.getTime() - today.getTime()) / 86400000
  );

  if (daysRemaining <= 30) return "Expiring Soon";

  return contract.status || "Active";
};

const ContractManagement = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [contracts, setContracts] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [contractCategories, setContractCategories] = useState([]);
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [lookupsLoading, setLookupsLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [pageError, setPageError] = useState("");
  const [lookupError, setLookupError] = useState("");
  const [formError, setFormError] = useState("");

  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [typeFilter, setTypeFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");

  const userDepartments = useMemo(() => {
    return Array.from(
      new Set(users.map((u) => u.department).filter(Boolean))
    ).sort();
  }, [users]);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  // Load Contract Type, Contract Category, and Users from their own APIs.
  const fetchLookups = useCallback(async () => {
    setLookupsLoading(true);
    setLookupError("");

    try {
      const [typeResponse, categoryResponse, userResponse] = await Promise.all([
        axios.get(API.types, { params: { per_page: 200 } }),
        axios.get(API.categories, { params: { per_page: 200 } }),
        axios.get(API.users).catch(() => ({ data: [] })),
      ]);

      const activeOnly = (item) =>
        item.is_active === undefined ||
        item.is_active === true ||
        item.is_active === 1 ||
        item.is_active === "1";

      setContractTypes(getRows(typeResponse).filter(activeOnly));
      setContractCategories(getRows(categoryResponse).filter(activeOnly));
      setUsers(getRows(userResponse));
    } catch (error) {
      setLookupError(getErrorMessage(error));
    } finally {
      setLookupsLoading(false);
    }
  }, []);

  // Load contract records. No local sample or fallback records are used.
  const fetchContracts = useCallback(async () => {
    setLoading(true);
    setPageError("");

    try {
      const response = await axios.get(API.contracts, {
        params: {
          search: search.trim() || undefined,
          contract_type_id: typeFilter || undefined,
          contract_category_id: categoryFilter || undefined,
          per_page: 200,
        },
      });

      setContracts(getRows(response));
    } catch (error) {
      setContracts([]);
      setPageError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, categoryFilter]);

  useEffect(() => {
    fetchLookups();
  }, [fetchLookups]);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const findTypeName = useCallback(
    (contract) => {
      const relation = contract.contractType ?? contract.contract_type;

      if (relation && typeof relation === "object") {
        return getLabel(relation);
      }

      const type = contractTypes.find(
        (item) =>
          String(item.id) === String(contract.contract_type_id)
      );

      return type ? getLabel(type) : "—";
    },
    [contractTypes]
  );

  const findCategoryName = useCallback(
    (contract) => {
      const relation =
        contract.contractCategory ?? contract.contract_category;

      if (relation && typeof relation === "object") {
        return getLabel(relation);
      }

      const category = contractCategories.find(
        (item) =>
          String(item.id) === String(contract.contract_category_id)
      );

      return category ? getLabel(category) : "—";
    },
    [contractCategories]
  );

  const filteredContracts = useMemo(() => {
    return contracts.filter((contract) => {
      const typeName = findTypeName(contract);
      const categoryName = findCategoryName(contract);

      const matchedUser = users.find(
        (u) => u.name === contract.owner || u.email === contract.owner
      );
      const userDept = contract.department || matchedUser?.department || "";

      const searchableText = [
        contract.contract_no,
        contract.title,
        contract.supplier,
        contract.owner,
        userDept,
        typeName,
        categoryName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesSearch = searchableText.includes(
        search.trim().toLowerCase()
      );

      const typeLower = typeName.toLowerCase();
      const status = getDateStatus(contract);

      const matchesTab =
        activeTab === "all" ||
        (activeTab === "contract" &&
          !typeLower.includes("amc") &&
          !typeLower.includes("warranty")) ||
        (activeTab === "amc" && typeLower.includes("amc")) ||
        (activeTab === "warranty" && typeLower.includes("warranty")) ||
        (activeTab === "renewal" &&
          ["Renewal Due", "Expiring Soon"].includes(status));

      const matchesDepartment =
        !departmentFilter ||
        userDept.toLowerCase() === departmentFilter.toLowerCase();

      return matchesSearch && matchesTab && matchesDepartment;
    });
  }, [
    contracts,
    users,
    findTypeName,
    findCategoryName,
    search,
    activeTab,
    departmentFilter,
  ]);

  const activeCount = contracts.filter(
    (contract) => getDateStatus(contract) === "Active"
  ).length;

  const amcCount = contracts.filter((contract) =>
    findTypeName(contract).toLowerCase().includes("amc")
  ).length;

  const warrantyCount = contracts.filter((contract) =>
    findTypeName(contract).toLowerCase().includes("warranty")
  ).length;

  const renewalCount = contracts.filter((contract) =>
    ["Renewal Due", "Expiring Soon"].includes(getDateStatus(contract))
  ).length;

  const totalValue = contracts.reduce(
    (total, contract) => total + Number(contract.value || 0),
    0
  );

  const openCreate = () => {
    setEditingId(null);
    setForm({
      ...EMPTY_FORM,
      contract_type_id: "",
      contract_category_id: "",
    });
    setFormError("");
    setShowModal(true);
  };

  const openEdit = (contract) => {
    setEditingId(contract.id);

    const matchedUser = users.find(
      (u) => u.name === contract.owner || u.email === contract.owner
    );
    const resolvedDepartment =
      contract.department || matchedUser?.department || "";

    setForm({
      ...EMPTY_FORM,
      ...contract,
      contract_type_id: String(contract.contract_type_id ?? ""),
      contract_category_id: String(contract.contract_category_id ?? ""),
      value: String(contract.value ?? ""),
      assets_covered: String(contract.assets_covered ?? 0),
      owner: contract.owner || "",
      department: resolvedDepartment,
      start_date: String(contract.start_date ?? "").slice(0, 10),
      end_date: String(contract.end_date ?? "").slice(0, 10),
      renewal_date: String(contract.renewal_date ?? "").slice(0, 10),
    });

    setFormError("");
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;

    setShowModal(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError("");
  };

  const updateField = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const handleOwnerChange = (ownerName) => {
    const selectedUser = users.find((u) => u.name === ownerName);
    setForm((previous) => ({
      ...previous,
      owner: ownerName,
      department: selectedUser?.department || "",
    }));
  };

  const saveContract = async (event) => {
    event.preventDefault();
    setFormError("");

    if (!form.contract_no.trim()) {
      setFormError("Contract Number is required.");
      return;
    }

    if (!form.title.trim()) {
      setFormError("Contract Title is required.");
      return;
    }

    if (!form.contract_type_id) {
      setFormError("Please select a Contract Type.");
      return;
    }

    if (!form.contract_category_id) {
      setFormError("Please select a Contract Category.");
      return;
    }

    if (!form.supplier.trim()) {
      setFormError("Supplier is required.");
      return;
    }

    if (!form.start_date || !form.end_date) {
      setFormError("Start Date and End Date are required.");
      return;
    }

    if (form.end_date < form.start_date) {
      setFormError("End Date cannot be before Start Date.");
      return;
    }

    if (form.renewal_date && form.renewal_date > form.end_date) {
      setFormError("Renewal Date cannot be after End Date.");
      return;
    }

    const payload = {
      contract_no: form.contract_no.trim(),
      title: form.title.trim(),
      contract_type_id: Number(form.contract_type_id),
      contract_category_id: Number(form.contract_category_id),
      supplier: form.supplier.trim(),
      start_date: form.start_date,
      end_date: form.end_date,
      renewal_date: form.renewal_date || null,
      value: Number(form.value || 0),
      status: form.status,
      owner: form.owner.trim() || null,
      department: form.department ? form.department.trim() : null,
      assets_covered: Number(form.assets_covered || 0),
      description: form.description.trim() || null,
    };

    setSaving(true);

    try {
      if (editingId) {
        await axios.put(`${API.contracts}/${editingId}`, payload);
      } else {
        await axios.post(API.contracts, payload);
      }

      setShowModal(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      await fetchContracts();
    } catch (error) {
      setFormError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const deleteContract = async (contract) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${contract.title}"?`
    );

    if (!confirmed) return;

    setPageError("");

    try {
      await axios.delete(`${API.contracts}/${contract.id}`);
      await fetchContracts();
    } catch (error) {
      setPageError(getErrorMessage(error));
    }
  };

  const statusClass = (status) => {
    if (["Renewal Due", "Expiring Soon"].includes(status)) {
      return "border-warning/20 bg-warning/10 text-warning";
    }

    if (status === "Expired") {
      return "border-error/20 bg-error/10 text-error";
    }

    if (["Draft", "Suspended"].includes(status)) {
      return "border-border bg-muted text-muted-foreground";
    }

    return "border-success/20 bg-success/10 text-success";
  };

  const statCards = [
    {
      label: "Total Contracts",
      value: contracts.length,
      icon: "FileText",
      color: "bg-primary/10 text-primary",
    },
    {
      label: "Active",
      value: activeCount,
      icon: "CircleCheck",
      color: "bg-success/10 text-success",
    },
    {
      label: "AMC",
      value: amcCount,
      icon: "Wrench",
      color: "bg-blue-500/10 text-blue-500",
    },
    {
      label: "Warranties",
      value: warrantyCount,
      icon: "ShieldCheck",
      color: "bg-purple-500/10 text-purple-500",
    },
    {
      label: "Renewals Due",
      value: renewalCount,
      icon: "CalendarClock",
      color: "bg-warning/10 text-warning",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="flex">
        <Sidebar
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() =>
            setSidebarCollapsed((previous) => !previous)
          }
        />

        <main
          className={`mt-16 min-w-0 flex-1 transition-layout ${
            sidebarCollapsed ? "ml-16" : "ml-72"
          }`}
        >
          <div className="p-4 md:p-8">
            <Breadcrumb />

            <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                  <Icon
                    name="FileText"
                    size={24}
                    className="text-primary"
                  />
                </div>

                <div>
                  <h1 className="text-2xl font-bold text-foreground md:text-3xl">
                    Contract Management
                  </h1>
                  <p className="mt-1 text-muted-foreground">
                    Manage contracts, categories, types, suppliers and renewals.
                  </p>
                </div>
              </div>

              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={openCreate}
                disabled={lookupsLoading}
              >
                Add Contract
              </Button>
            </div>

            {pageError && (
              <div className="mb-5 rounded-lg border border-error/20 bg-error/10 p-4 text-sm text-error">
                <p>{pageError}</p>
                <button
                  type="button"
                  className="mt-2 underline"
                  onClick={fetchContracts}
                >
                  Retry
                </button>
              </div>
            )}

            {lookupError && (
              <div className="mb-5 rounded-lg border border-warning/20 bg-warning/10 p-4 text-sm">
                <p className="font-semibold">
                  Unable to load Contract Types or Contract Categories.
                </p>
                <p className="mt-1">{lookupError}</p>
                <button
                  type="button"
                  className="mt-2 underline"
                  onClick={fetchLookups}
                >
                  Retry
                </button>
              </div>
            )}

            <div className="mb-8 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-5">
              {statCards.map((card) => (
                <div
                  key={card.label}
                  className="rounded-xl border border-border bg-card p-5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {card.label}
                      </p>
                      <p className="mt-2 text-2xl font-bold text-foreground">
                        {card.value}
                      </p>
                    </div>

                    <div
                      className={`flex h-11 w-11 items-center justify-center rounded-lg ${card.color}`}
                    >
                      <Icon name={card.icon} size={21} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="overflow-hidden rounded-xl border border-border bg-card">
              <div className="space-y-4 border-b border-border p-5">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex flex-wrap gap-1 rounded-lg bg-muted/40 p-1">
                    {[
                      ["all", "All"],
                      ["contract", "Contracts"],
                      ["amc", "AMC"],
                      ["warranty", "Warranties"],
                      ["renewal", "Renewals"],
                    ].map(([id, label]) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setActiveTab(id)}
                        className={`rounded-md px-3 py-2 text-sm font-medium ${
                          activeTab === id
                            ? "bg-background text-primary shadow-sm"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  <div className="relative w-full sm:w-72">
                    <Icon
                      name="Search"
                      size={17}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    />
                    <input
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Search contracts..."
                      className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <select
                    value={typeFilter}
                    onChange={(event) => setTypeFilter(event.target.value)}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                  >
                    <option value="">All Contract Types</option>
                    {contractTypes.map((item) => (
                      <option key={item.id} value={item.id}>
                        {getLabel(item)}
                      </option>
                    ))}
                  </select>

                  <select
                    value={categoryFilter}
                    onChange={(event) =>
                      setCategoryFilter(event.target.value)
                    }
                    className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                  >
                    <option value="">All Contract Categories</option>
                    {contractCategories.map((item) => (
                      <option key={item.id} value={item.id}>
                        {getLabel(item)}
                      </option>
                    ))}
                  </select>

                  <select
                    value={departmentFilter}
                    onChange={(event) =>
                      setDepartmentFilter(event.target.value)
                    }
                    className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                  >
                    <option value="">All Departments (from Users)</option>
                    {userDepartments.map((dept) => (
                      <option key={dept} value={dept}>
                        {dept}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[1200px]">
                  <thead>
                    <tr className="border-b border-border bg-muted/20">
                      {[
                        "Contract",
                        "Type",
                        "Category",
                        "Supplier",
                        "Owner & Dept",
                        "Period",
                        "Renewal",
                        "Value",
                        "Assets",
                        "Status",
                        "Actions",
                      ].map((heading) => (
                        <th
                          key={heading}
                          className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td
                          colSpan={11}
                          className="py-16 text-center text-muted-foreground"
                        >
                          Loading contracts...
                        </td>
                      </tr>
                    ) : filteredContracts.length === 0 ? (
                      <tr>
                        <td colSpan={11} className="py-16 text-center">
                          <Icon
                            name="SearchX"
                            size={26}
                            className="mx-auto text-muted-foreground"
                          />
                          <h3 className="mt-4 font-semibold text-foreground">
                            No contracts found
                          </h3>
                          <p className="mt-1 text-sm text-muted-foreground">
                            No matching records were returned by the API.
                          </p>
                        </td>
                      </tr>
                    ) : (
                      filteredContracts.map((contract) => {
                        const status = getDateStatus(contract);

                        return (
                          <tr
                            key={contract.id}
                            className="border-b border-border last:border-b-0 hover:bg-muted/20"
                          >
                            <td className="px-5 py-5">
                              <p className="font-medium text-foreground">
                                {contract.title}
                              </p>
                              <p className="mt-1 text-xs text-muted-foreground">
                                {contract.contract_no}
                              </p>
                            </td>

                            <td className="px-5 py-5 text-sm">
                              {findTypeName(contract)}
                            </td>

                            <td className="px-5 py-5 text-sm">
                              {findCategoryName(contract)}
                            </td>

                            <td className="px-5 py-5 text-sm">
                              {contract.supplier || "—"}
                            </td>

                            <td className="px-5 py-5 text-sm">
                              <p className="font-medium text-foreground">
                                {contract.owner || "—"}
                              </p>
                              {(() => {
                                const matched = users.find(
                                  (u) =>
                                    u.name === contract.owner ||
                                    u.email === contract.owner
                                );
                                const dept =
                                  contract.department || matched?.department;
                                return dept ? (
                                  <p className="mt-0.5 text-xs text-muted-foreground">
                                    {dept}
                                  </p>
                                ) : null;
                              })()}
                            </td>

                            <td className="whitespace-nowrap px-5 py-5 text-sm">
                              {formatDate(contract.start_date)} →{" "}
                              {formatDate(contract.end_date)}
                            </td>

                            <td className="whitespace-nowrap px-5 py-5 text-sm">
                              {formatDate(contract.renewal_date)}
                            </td>

                            <td className="whitespace-nowrap px-5 py-5 text-sm font-medium">
                              {formatMoney(contract.value)}
                            </td>

                            <td className="px-5 py-5 text-sm">
                              {contract.assets_covered ?? 0}
                            </td>

                            <td className="px-5 py-5">
                              <span
                                className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-medium ${statusClass(status)}`}
                              >
                                {status}
                              </span>
                            </td>

                            <td className="px-5 py-5">
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  title="Edit contract"
                                  onClick={() => openEdit(contract)}
                                  className="rounded-lg p-2 text-primary hover:bg-muted"
                                >
                                  <Icon name="Pencil" size={17} />
                                </button>

                                <button
                                  type="button"
                                  title="Delete contract"
                                  onClick={() => deleteContract(contract)}
                                  className="rounded-lg p-2 text-error hover:bg-error/10"
                                >
                                  <Icon name="Trash2" size={17} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-6 xl:grid-cols-3">
              {[
                {
                  title: "Contract Types",
                  description:
                    "Types are managed in the Contract Type module and loaded into this page.",
                  icon: "FileText",
                  count: contractTypes.length,
                  route: "/contract-types",
                },
                {
                  title: "Contract Categories",
                  description:
                    "Categories are managed in the Contract Category module and loaded into this page.",
                  icon: "FolderTree",
                  count: contractCategories.length,
                  route: "/contract-categories",
                },
                {
                  title: "Contract Renewals",
                  description:
                    "Review agreements that are due for renewal or approaching expiration.",
                  icon: "CalendarClock",
                  count: renewalCount,
                  route: null,
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="rounded-xl border border-border bg-card p-6"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                    <Icon
                      name={item.icon}
                      size={20}
                      className="text-primary"
                    />
                  </div>

                  <h3 className="mt-4 font-semibold text-foreground">
                    {item.title}
                  </h3>

                  <p className="mt-2 text-sm text-muted-foreground">
                    {item.description}
                  </p>

                  <p className="mt-3 text-sm font-medium">
                    Records: {item.count}
                  </p>

                  {item.route && (
                    <button
                      type="button"
                      onClick={() =>
                        (window.location.href = item.route)
                      }
                      className="mt-3 text-sm font-medium text-primary hover:underline"
                    >
                      Open module
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-[100] flex">
          <button
            type="button"
            aria-label="Close modal"
            onClick={closeModal}
            className="absolute inset-0 bg-black/50"
          />

          <div className="relative ml-auto flex h-full w-full max-w-2xl flex-col bg-background shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-border bg-card px-6 py-5">
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  {editingId ? "Edit Contract" : "Add Contract"}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Contract Type and Category are loaded from their own modules.
                </p>
              </div>

              <button
                type="button"
                onClick={closeModal}
                className="rounded-lg p-2 hover:bg-muted"
                aria-label="Close"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <form
              onSubmit={saveContract}
              className="min-h-0 flex-1 overflow-y-auto"
            >
              <div className="space-y-7 p-6">
                {formError && (
                  <div className="rounded-lg border border-error/20 bg-error/10 p-4 text-sm text-error">
                    {formError}
                  </div>
                )}

                <section>
                  <h3 className="mb-4 font-semibold text-foreground">
                    Contract Information
                  </h3>

                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium">
                        Contract Number *
                      </label>
                      <input
                        required
                        value={form.contract_no}
                        onChange={(event) =>
                          updateField("contract_no", event.target.value)
                        }
                        className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium">
                        Contract Type *
                      </label>
                      <select
                        required
                        value={form.contract_type_id}
                        onChange={(event) =>
                          updateField("contract_type_id", event.target.value)
                        }
                        className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                      >
                        <option value="">Select Contract Type</option>
                        {contractTypes.map((item) => (
                          <option key={item.id} value={item.id}>
                            {getLabel(item)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="mb-2 block text-sm font-medium">
                        Contract Title *
                      </label>
                      <input
                        required
                        value={form.title}
                        onChange={(event) =>
                          updateField("title", event.target.value)
                        }
                        className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium">
                        Contract Category *
                      </label>
                      <select
                        required
                        value={form.contract_category_id}
                        onChange={(event) =>
                          updateField("contract_category_id", event.target.value)
                        }
                        className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                      >
                        <option value="">Select Contract Category</option>
                        {contractCategories.map((item) => (
                          <option key={item.id} value={item.id}>
                            {getLabel(item)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium">
                        Supplier *
                      </label>
                      <input
                        required
                        value={form.supplier}
                        onChange={(event) =>
                          updateField("supplier", event.target.value)
                        }
                        className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                      />
                    </div>
                  </div>
                </section>

                <section>
                  <h3 className="mb-4 font-semibold text-foreground">
                    Contract Period
                  </h3>

                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium">
                        Start Date *
                      </label>
                      <input
                        required
                        type="date"
                        value={form.start_date}
                        onChange={(event) =>
                          updateField("start_date", event.target.value)
                        }
                        className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium">
                        End Date *
                      </label>
                      <input
                        required
                        type="date"
                        min={form.start_date || undefined}
                        value={form.end_date}
                        onChange={(event) =>
                          updateField("end_date", event.target.value)
                        }
                        className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium">
                        Renewal Date
                      </label>
                      <input
                        type="date"
                        min={form.start_date || undefined}
                        max={form.end_date || undefined}
                        value={form.renewal_date}
                        onChange={(event) =>
                          updateField("renewal_date", event.target.value)
                        }
                        className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium">
                        Assets Covered
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={form.assets_covered}
                        onChange={(event) =>
                          updateField("assets_covered", event.target.value)
                        }
                        className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                      />
                    </div>
                  </div>
                </section>

                <section>
                  <h3 className="mb-4 font-semibold text-foreground">
                    Commercial & Ownership
                  </h3>

                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium">
                        Contract Value (INR)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.value}
                        onChange={(event) =>
                          updateField("value", event.target.value)
                        }
                        className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium">
                        Owner
                      </label>
                      <select
                        value={form.owner}
                        onChange={(event) =>
                          handleOwnerChange(event.target.value)
                        }
                        className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                      >
                        <option value="">Select Contract Owner</option>
                        {users.map((user) => (
                          <option key={user.id} value={user.name}>
                            {user.name} {user.department ? `(${user.department})` : ""}
                          </option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Users fetched dynamically from User Management.
                      </p>
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium">
                        Department
                      </label>
                      <input
                        readOnly
                        value={form.department || ""}
                        placeholder={
                          form.owner
                            ? "No department assigned to user"
                            : "Auto-filled when owner is selected"
                        }
                        className="w-full rounded-lg border border-border bg-muted/60 px-3 py-2.5 text-sm text-foreground cursor-not-allowed"
                      />
                      <p className="mt-1 text-xs text-muted-foreground">
                        Department is auto-populated and saved from the selected user.
                      </p>
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium">
                        Status *
                      </label>
                      <select
                        required
                        value={form.status}
                        onChange={(event) =>
                          updateField("status", event.target.value)
                        }
                        className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                      >
                        {STATUSES.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="mb-2 block text-sm font-medium">
                        Description / Notes
                      </label>
                      <textarea
                        rows={4}
                        value={form.description}
                        onChange={(event) =>
                          updateField("description", event.target.value)
                        }
                        className="w-full resize-y rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                      />
                    </div>
                  </div>
                </section>
              </div>

              <div className="sticky bottom-0 flex justify-end gap-3 border-t border-border bg-card px-6 py-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  iconName={editingId ? "Save" : "Plus"}
                  iconPosition="left"
                  disabled={saving || lookupsLoading}
                >
                  {saving
                    ? "Saving..."
                    : editingId
                    ? "Update Contract"
                    : "Add Contract"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractManagement;
